import {
  agentCreateSchema,
  agentPatchSchema,
  insurerCreateSchema,
  insurerPatchSchema,
  PERIODICITIES,
  PRODUCT_FAMILIES,
  productLineCreateSchema,
  productLinePatchSchema,
} from '@beta-assur/shared';
import { useState } from 'react';
import { ActionError } from '../../components/states';
import { useT } from '../../lib/i18n';
import { useApiMutation } from '../../lib/query';
import { useReferentials } from '../../lib/referentials';
import type { Agent, Insurer, ProductLine } from '../../lib/types';
import { CrudSection } from './crud';

// Assureurs, lignes de produit et agents (BA-026, spec 08 §8).

const str = (value: unknown) => (value === null || value === undefined ? '' : String(value));

/** Logo de l'assureur sur la page assuré (BA-052) : PNG ou JPEG de 200 Ko au plus. */
function LogoUpload({ insurerId }: { insurerId: string }) {
  const t = useT();
  const [done, setDone] = useState(false);
  const upload = useApiMutation<FormData>('PUT', `/v1/insurers/${insurerId}/logo`, {
    onSuccess: () => setDone(true),
  });
  return (
    <span className="flex flex-col gap-1">
      <label className="cursor-pointer text-xs text-primary underline">
        {t.settings.uploadLogo}
        <input
          type="file"
          accept="image/png,image/jpeg"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setDone(false);
            const form = new FormData();
            form.set('file', file);
            upload.mutate(form);
          }}
        />
      </label>
      {done ? (
        <span role="status" className="text-xs text-success">
          {t.common.saved}
        </span>
      ) : null}
      <ActionError error={upload.error} />
    </span>
  );
}

export function InsurersSection() {
  const t = useT();
  return (
    <CrudSection<Insurer & Record<string, unknown>>
      title={t.settings.insurers}
      path="/v1/insurers"
      createSchema={insurerCreateSchema}
      patchSchema={insurerPatchSchema}
      columns={[
        { label: t.settings.displayName, render: (r) => r.displayName },
        { label: t.settings.code, render: (r) => r.code },
        { label: t.settings.legalName, render: (r) => r.legalName },
        { label: t.settings.senderName, render: (r) => r.senderName ?? '-' },
        { label: t.settings.logo, render: (r) => <LogoUpload insurerId={r.id} /> },
      ]}
      fields={[
        { key: 'code', label: t.settings.code, immutable: true, dir: 'ltr' },
        { key: 'displayName', label: t.settings.displayName },
        { key: 'legalName', label: t.settings.legalName },
        { key: 'senderName', label: t.settings.senderName, nullable: true, dir: 'ltr' },
        { key: 'legalMentions', label: t.settings.legalMentions, type: 'textarea', nullable: true },
      ]}
      toForm={(r) => ({
        code: str(r?.code),
        displayName: str(r?.displayName),
        legalName: str(r?.legalName),
        senderName: str(r?.senderName),
        legalMentions: str(r?.legalMentions),
      })}
    />
  );
}

export function ProductLinesSection() {
  const t = useT();
  const refs = useReferentials();
  return (
    <CrudSection<ProductLine & Record<string, unknown>>
      title={t.settings.productLines}
      path="/v1/product-lines"
      createSchema={productLineCreateSchema}
      patchSchema={productLinePatchSchema}
      columns={[
        { label: t.settings.labelFr, render: (r) => r.labelFr },
        { label: t.settings.code, render: (r) => r.code },
        { label: t.portfolio.insurer, render: (r) => refs.insurerName(r.insurerId) },
        { label: t.settings.family, render: (r) => t.families[r.family] },
        { label: t.settings.graceDays, render: (r) => r.graceDays },
        {
          label: t.settings.suspensionDays,
          render: (r) => r.suspensionDays ?? t.settings.noSuspension,
        },
      ]}
      fields={[
        {
          key: 'insurerId',
          label: t.portfolio.insurer,
          type: 'select',
          immutable: true,
          options: [
            { value: '', label: '-' },
            ...refs.insurers.map((i) => ({ value: i.id, label: i.displayName })),
          ],
        },
        { key: 'code', label: t.settings.code, immutable: true, dir: 'ltr' },
        { key: 'labelFr', label: t.settings.labelFr },
        { key: 'labelAr', label: t.settings.labelAr, nullable: true, dir: 'rtl' },
        {
          key: 'family',
          label: t.settings.family,
          type: 'select',
          options: PRODUCT_FAMILIES.map((f) => ({ value: f, label: t.families[f] })),
        },
        {
          key: 'defaultPeriodicity',
          label: t.settings.defaultPeriodicity,
          type: 'select',
          options: PERIODICITIES.map((p) => ({ value: p, label: t.periodicity[p] })),
        },
        { key: 'graceDays', label: t.settings.graceDays, type: 'number' },
        { key: 'suspensionDays', label: t.settings.suspensionDays, type: 'number', nullable: true },
        // Préavis et attestation propres à la branche (BA-079, BA-081) ; vide : réglage du cabinet.
        {
          key: 'notices.dueNoticeDays',
          label: t.settings.dueNoticeDays,
          none: t.settings.inherited,
          type: 'notice',
          nullable: true,
        },
        {
          key: 'notices.contractEndNoticeDays',
          label: t.settings.contractEndNoticeDays,
          none: t.settings.inherited,
          type: 'notice',
          nullable: true,
        },
        {
          key: 'notices.attestationAfterPayment',
          label: t.settings.attestationAfterPaymentShort,
          type: 'boolean',
          nullable: true,
        },
      ]}
      toForm={(r) => ({
        insurerId: str(r?.insurerId ?? refs.insurers[0]?.id),
        code: str(r?.code),
        labelFr: str(r?.labelFr),
        labelAr: str(r?.labelAr),
        family: str(r?.family ?? 'auto'),
        defaultPeriodicity: str(r?.defaultPeriodicity ?? 'annual'),
        graceDays: str(r?.graceDays ?? 0),
        suspensionDays: str(r?.suspensionDays),
        'notices.dueNoticeDays': str(r?.notices.dueNoticeDays),
        'notices.contractEndNoticeDays': str(r?.notices.contractEndNoticeDays),
        'notices.attestationAfterPayment': str(r?.notices.attestationAfterPayment),
      })}
    />
  );
}

export function AgentsSection() {
  const t = useT();
  return (
    <CrudSection<Agent & Record<string, unknown>>
      title={t.settings.agents}
      path="/v1/agents"
      createSchema={agentCreateSchema}
      patchSchema={agentPatchSchema}
      columns={[
        { label: t.settings.name, render: (r) => r.name },
        { label: t.settings.code, render: (r) => r.code },
        { label: t.settings.branch, render: (r) => r.branch ?? '-' },
        { label: t.settings.phone, render: (r) => <span dir="ltr">{r.phone ?? '-'}</span> },
        { label: t.settings.email, render: (r) => r.email ?? '-' },
      ]}
      fields={[
        { key: 'code', label: t.settings.code, immutable: true, dir: 'ltr' },
        { key: 'name', label: t.settings.name },
        { key: 'branch', label: t.settings.branch, nullable: true },
        { key: 'phone', label: t.settings.phone, nullable: true, dir: 'ltr' },
        { key: 'email', label: t.settings.email, nullable: true, dir: 'ltr' },
        { key: 'userId', label: t.settings.linkedUser, nullable: true, dir: 'ltr' },
      ]}
      toForm={(r) => ({
        code: str(r?.code),
        name: str(r?.name),
        branch: str(r?.branch),
        phone: str(r?.phone),
        email: str(r?.email),
        userId: str(r?.userId),
      })}
    />
  );
}
